import { type CSSToken, token } from '@atlaskit/tokens';
import type { IconColor } from '@atlaskit/tokens/css-type-schema';

// skill border/accent colors that map to a color enum defined in integration service
// https://bitbucket.org/atlassian/integrations-service/src/5ebc708689a91d0dc8c35bc8dda1395c19fadb9a/integrations-service-skills-domain/src/main/kotlin/io/atlassian/skills/schema/SkillMetadata.kt#lines-7
// TODO: move to a skills types/integration service package
export type SkillColor =
	| 'LIME'
	| 'RED'
	| 'ORANGE'
	| 'YELLOW'
	| 'GREEN'
	| 'TEAL'
	| 'BLUE'
	| 'PURPLE'
	| 'MAGENTA'
	| 'GRAY'
	| 'DEFAULT';

type SkillColorMap = Record<SkillColor, { default: CSSToken; inverse?: CSSToken; icon: IconColor }>;

// mapping of skill color enum to ADS color tokens. Use inverse colors for dark backgrounds
export const SKILL_COLOR_MAP: SkillColorMap = {
	LIME: {
		default: token('color.border.accent.lime'),
		icon: token('color.icon.accent.lime'),
	},
	RED: {
		default: token('color.border.accent.red'),
		icon: token('color.icon.accent.red'),
	},
	ORANGE: {
		default: token('color.border.accent.orange'),
		icon: token('color.icon.accent.orange'),
	},
	YELLOW: {
		default: token('color.border.accent.yellow'),
		icon: token('color.icon.accent.yellow'),
	},
	GREEN: {
		default: token('color.border.accent.green'),
		icon: token('color.icon.accent.green'),
	},
	TEAL: {
		default: token('color.border.accent.teal'),
		icon: token('color.icon.accent.teal'),
	},
	BLUE: {
		default: token('color.border.accent.blue'),
		icon: token('color.icon.accent.blue'),
	},
	PURPLE: {
		default: token('color.border.accent.purple'),
		icon: token('color.icon.accent.purple'),
	},
	MAGENTA: {
		default: token('color.border.accent.magenta'),
		icon: token('color.icon.accent.magenta'),
	},
	GRAY: {
		default: token('color.border.accent.gray'),
		icon: token('color.icon.accent.gray'),
	},
	DEFAULT: {
		default: token('color.border'),
		inverse: token('color.border.inverse'),
		icon: token('color.icon.subtle'),
	},
};

/**
 * @param color skill color
 * @param invertColors if the skill is rendered on a dark surface
 * @returns border design token
 */
export const getBorderColor = (color?: SkillColor, invertColors?: boolean) => {
	if (color) {
		const colorObject = SKILL_COLOR_MAP[color];
		const colorType = invertColors ? 'inverse' : 'default';
		return colorObject?.[colorType] ?? colorObject?.default ?? token('color.border');
	}

	return invertColors ? token('color.border.inverse') : token('color.border');
};

/**
 * @param color skill color
 * @param invertColors if the skill is rendered on a dark surface
 * @returns icon design token
 */
export const getIconColor = (color?: SkillColor, invertColors?: boolean): IconColor => {
	if (color) {
		const colorObject = SKILL_COLOR_MAP[color];

		if (invertColors && color === 'DEFAULT') {
			return token('color.icon.inverse');
		}

		return colorObject?.icon ?? token('color.icon.subtle');
	}

	return invertColors ? token('color.icon.inverse') : token('color.icon.subtle');
};
